<!-- Real CRUD for scheduled analytics reports - replaces the old visual-only wizard mock. Styled
     to match the Saved Views / Exports utility screen (module switcher + card header + table).
     SETTE redesign (CLIPPING-598): token-by-token restyle, no logic change. -->
<div class="space-y-6">
	@if (toastMessage) {
	<div class="fixed bottom-5 right-5 z-50 bg-ink text-white px-4 py-3 rounded-[14px] shadow-toast text-xs font-medium flex items-center gap-2">
		<mat-icon class="!w-4 !h-4 !text-base text-positive-soft">check_circle</mat-icon>
		<span>{{ toastMessage }}</span>
	</div>
	}

	<div class="bg-surface rounded-2xl border border-line p-5 sm:p-6 flex items-start justify-between gap-4">
		<div>
			<div class="flex items-center gap-2">
				<h1 class="page-title">{{ 'ANALYTICS_NAV.REPORTS' | translate }}</h1>
				<span class="text-xs font-medium px-2 py-0.5 rounded-full bg-blue-tint text-blue">{{ 'ANALYTICS_REPORTS.SCHEDULED_EMAIL_BADGE' | translate }}</span>
			</div>
			<p class="text-xs text-ink-subtle mt-1 max-w-2xl">
				{{ 'ANALYTICS_REPORTS.REPORTS_DESC' | translate }}
			</p>
		</div>
		<button
			type="button"
			(click)="openCreateModal()"
			[disabled]="reportsLimitReached"
			[title]="reportsLimitReached ? ('ANALYTICS_REPORTS.MAX_REPORTS_REACHED' | translate: { max: maxReports }) : ''"
			class="shrink-0 inline-flex items-center gap-1.5 h-10 px-[18px] bg-blue hover:bg-blue-hover text-on-blue font-medium rounded-full text-[15px] transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed">
			<mat-icon class="!w-4 !h-4 !text-base">add</mat-icon>
			<span>{{ 'ANALYTICS_REPORTS.NEW_REPORT' | translate }}</span>
		</button>
	</div>
	@if (reportsLimitReached) {
	<p class="text-[11px] text-ink-subtle">{{ 'ANALYTICS_REPORTS.MAX_REPORTS_REACHED' | translate: { max: maxReports } }}</p>
	}

	<!-- Module Navigation Switcher -->
	<div class="inline-flex bg-surface-gray p-1 rounded-full">
		<button
			type="button"
			(click)="setSection('reports')"
			class="flex items-center gap-2 h-9 px-4 rounded-full text-[13px] font-medium transition-colors cursor-pointer"
			[class]="activeSection === 'reports' ? 'bg-surface text-ink shadow-[0_1px_3px_rgba(28,29,33,0.10)]' : 'text-ink-muted hover:text-ink'">
			<mat-icon class="!w-4 !h-4 !text-base">bar_chart</mat-icon>
			<span>{{ 'ANALYTICS_REPORTS.REPORTS_TAB' | translate: { count: reports.length } }}</span>
		</button>
		<button
			type="button"
			(click)="setSection('history')"
			class="flex items-center gap-2 h-9 px-4 rounded-full text-[13px] font-medium transition-colors cursor-pointer"
			[class]="activeSection === 'history' ? 'bg-surface text-ink shadow-[0_1px_3px_rgba(28,29,33,0.10)]' : 'text-ink-muted hover:text-ink'">
			<mat-icon class="!w-4 !h-4 !text-base">history</mat-icon>
			<span>{{ 'ANALYTICS_REPORTS.SEND_HISTORY_TAB' | translate }}</span>
		</button>
	</div>

	<!-- SECTION: REPORTS -->
	@if (activeSection === 'reports') {
	<div class="bg-surface rounded-2xl border border-line overflow-hidden">
		@if (reportsLoading) {
		<div class="p-10 text-center caption text-ink-subtle">{{ 'ANALYTICS_REPORTS.LOADING_REPORTS' | translate }}</div>
		} @else {
		<div class="overflow-x-auto">
			<table class="w-full text-left border-collapse text-xs">
				<thead>
					<tr class="bg-page border-b border-line overline-label text-ink-subtle">
						<th class="h-10 px-4 sm:px-6">{{ 'ANALYTICS_REPORTS.TABLE_NAME' | translate }}</th>
						<th class="h-10 px-3">{{ 'ANALYTICS_REPORTS.TABLE_SCREENS' | translate }}</th>
						<th class="h-10 px-3">{{ 'ANALYTICS_REPORTS.TABLE_FREQUENCY' | translate }}</th>
						<th class="h-10 px-3">{{ 'ANALYTICS_REPORTS.TABLE_RECIPIENTS' | translate }}</th>
						<th class="h-10 px-3">{{ 'ANALYTICS_REPORTS.TABLE_CREATED' | translate }}</th>
						<th class="h-10 px-4 sm:px-6 text-right">{{ 'ANALYTICS_REPORTS.TABLE_ACTIONS' | translate }}</th>
					</tr>
				</thead>
				<tbody class="font-medium">
					@for (row of reports; track row.id) {
					<tr class="border-t border-line hover:bg-surface-blue transition-colors align-top">
						<td class="py-3 px-4 sm:px-6 font-medium text-ink">{{ row.name }}</td>
						<td class="py-3 px-3">
							<div class="flex flex-wrap gap-1">
								@for (label of row.screenLabels; track label) {
								<span class="text-[10px] font-medium px-1.5 py-0.5 rounded-lg bg-surface-gray text-ink-muted">{{ label }}</span>
								}
							</div>
						</td>
						<td class="py-3 px-3 text-ink-muted">{{ row.frequencyLabel }}</td>
						<td class="py-3 px-3 text-ink-subtle" [title]="row.recipients.join(', ')">{{ row.recipients.length }} {{ (row.recipients.length === 1 ? 'ANALYTICS_REPORTS.RECIPIENT_SINGULAR' : 'ANALYTICS_REPORTS.RECIPIENT_PLURAL') | translate }}</td>
						<td class="py-3 px-3 text-ink-subtle">{{ row.createdAtLabel }}</td>
						<td class="py-3 px-4 sm:px-6 text-right">
							<div class="flex items-center justify-end gap-1.5">
								<button
									type="button"
									(click)="previewReport(row)"
									[disabled]="previewingId === row.id"
									[title]="'ANALYTICS_REPORTS.PREVIEW_TOOLTIP' | translate"
									class="inline-flex items-center gap-1 bg-surface hover:bg-surface-gray text-ink border border-line-strong font-medium h-8 px-2.5 rounded-full text-xs transition-colors cursor-pointer disabled:opacity-50">
									<mat-icon class="!w-3.5 !h-3.5 !text-sm text-ink-subtle">picture_as_pdf</mat-icon>
									<span>{{ (previewingId === row.id ? 'ANALYTICS_REPORTS.GENERATING' : 'ANALYTICS_REPORTS.PREVIEW') | translate }}</span>
								</button>
								<button
									type="button"
									(click)="sendNow(row)"
									[disabled]="sendingId === row.id"
									[title]="'ANALYTICS_REPORTS.SEND_NOW_TOOLTIP' | translate"
									class="inline-flex items-center gap-1 bg-surface hover:bg-surface-gray text-ink border border-line-strong font-medium h-8 px-2.5 rounded-full text-xs transition-colors cursor-pointer disabled:opacity-50">
									<mat-icon class="!w-3.5 !h-3.5 !text-sm text-ink-subtle">send</mat-icon>
									<span>{{ (sendingId === row.id ? 'ANALYTICS_REPORTS.SENDING' : 'ANALYTICS_REPORTS.SEND_NOW') | translate }}</span>
								</button>
								<button
									type="button"
									(click)="deleteReport(row)"
									[title]="'BUTTON.DELETE' | translate"
									class="inline-flex items-center justify-center w-8 h-8 rounded-full hover:bg-negative-tint text-negative transition-colors cursor-pointer">
									<mat-icon class="!w-4 !h-4 !text-base">delete</mat-icon>
								</button>
							</div>
						</td>
					</tr>
					}
				</tbody>
			</table>
		</div>
		@if (reports.length === 0) {
		<div class="p-10 text-center caption text-ink-subtle">{{ 'ANALYTICS_REPORTS.NO_REPORTS_YET' | translate }}</div>
		}
		}
	</div>
	}

	<!-- SECTION: SEND HISTORY -->
	@if (activeSection === 'history') {
	<div class="bg-surface rounded-2xl border border-line overflow-hidden">
		<div class="p-4 sm:px-6 border-b border-line">
			<h2 class="section-title">{{ 'ANALYTICS_REPORTS.SEND_HISTORY_TITLE' | translate }}</h2>
			<p class="text-xs text-ink-subtle">{{ 'ANALYTICS_REPORTS.SEND_HISTORY_DESC' | translate }}</p>
		</div>
		@if (historyLoading) {
		<div class="p-10 text-center caption text-ink-subtle">{{ 'ANALYTICS_REPORTS.LOADING_HISTORY' | translate }}</div>
		} @else {
		<div class="overflow-x-auto">
			<table class="w-full text-left border-collapse text-xs">
				<thead>
					<tr class="bg-page border-b border-line overline-label text-ink-subtle">
						<th class="h-10 px-4 sm:px-6">{{ 'ANALYTICS_REPORTS.TABLE_REPORT' | translate }}</th>
						<th class="h-10 px-3">{{ 'ANALYTICS_FILTER_BAR.PERIOD_LABEL' | translate }}</th>
						<th class="h-10 px-3">{{ 'ANALYTICS_REPORTS.TABLE_RECIPIENTS' | translate }}</th>
						<th class="h-10 px-3">{{ 'ANALYTICS_REPORTS.TABLE_STATUS' | translate }}</th>
						<th class="h-10 px-4 sm:px-6">{{ 'ANALYTICS_REPORTS.TABLE_SENT_AT' | translate }}</th>
					</tr>
				</thead>
				<tbody class="font-medium">
					@for (h of history; track h.id) {
					<tr class="border-t border-line hover:bg-surface-blue transition-colors">
						<td class="py-3 px-4 sm:px-6 font-medium text-ink">{{ h.reportName }}</td>
						<td class="py-3 px-3 text-ink-subtle">{{ h.periodLabel }}</td>
						<td class="py-3 px-3 text-ink-subtle">{{ h.recipientCount }}</td>
						<td class="py-3 px-3">
							@if (h.success) {
							<span class="text-[10px] font-medium px-2 py-0.5 rounded-full bg-positive-tint text-positive">{{ 'ANALYTICS_REPORTS.STATUS_SENT' | translate }}</span>
							} @else {
							<span class="text-[10px] font-medium px-2 py-0.5 rounded-full bg-negative-tint text-negative" [title]="h.errorMessage || ''">{{ 'ANALYTICS_REPORTS.STATUS_FAILED' | translate }}</span>
							}
						</td>
						<td class="py-3 px-4 sm:px-6 text-ink-subtle">{{ h.createdAtLabel }}</td>
					</tr>
					}
				</tbody>
			</table>
		</div>
		@if (history.length === 0) {
		<div class="p-10 text-center caption text-ink-subtle">{{ 'ANALYTICS_REPORTS.NO_HISTORY_YET' | translate }}</div>
		}
		}
	</div>
	}

	<!-- MODAL: CREATE REPORT -->
	@if (createModalOpen) {
	<div class="fixed inset-0 z-50 bg-ink/60 backdrop-blur-xs flex items-center justify-center p-4">
		<div class="bg-surface rounded-2xl shadow-pop border border-line w-full max-w-lg overflow-hidden text-xs max-h-[90vh] flex flex-col">
			<div class="px-6 py-4 border-b border-line flex items-center justify-between bg-page shrink-0">
				<h3 class="item-title text-ink">{{ 'ANALYTICS_REPORTS.NEW_SCHEDULED_REPORT_TITLE' | translate }}</h3>
				<button type="button" (click)="closeCreateModal()" class="w-8 h-8 rounded-full hover:bg-surface-gray text-ink-subtle hover:text-ink flex items-center justify-center transition-colors cursor-pointer">
					<mat-icon class="!w-4 !h-4 !text-base">close</mat-icon>
				</button>
			</div>

			<form (ngSubmit)="submitCreate()" class="p-6 space-y-5 overflow-y-auto">
				<div>
					<label class="block text-xs font-medium text-ink mb-1">{{ 'ANALYTICS_REPORTS.REPORT_NAME_LABEL' | translate }} <span class="text-negative">*</span></label>
					<input
						type="text"
						required
						cdkFocusInitial
						[(ngModel)]="createName"
						name="createName"
						[placeholder]="'ANALYTICS_REPORTS.NAME_PLACEHOLDER' | translate"
						class="w-full bg-surface border border-line-strong rounded-xl px-3 py-2 text-xs text-ink focus-visible:outline-2 focus-visible:outline-focus focus-visible:outline-offset-2 font-medium" />
				</div>

				<div>
					<label class="block text-xs font-medium text-ink mb-1.5">{{ 'ANALYTICS_REPORTS.SCREENS_LABEL' | translate }} <span class="text-negative">*</span></label>
					<div class="grid grid-cols-2 gap-1.5">
						@for (screen of allScreens; track screen) {
						<label class="flex items-center gap-2 px-2.5 py-1.5 rounded-xl border cursor-pointer text-xs font-medium transition-colors"
							[class]="createScreens.includes(screen) ? 'bg-blue-tint border-blue text-blue' : 'bg-surface border-line text-ink-muted hover:bg-surface-gray'">
							<input type="checkbox" [checked]="createScreens.includes(screen)" (change)="toggleCreateScreen(screen)" class="accent-blue" />
							<span>{{ screenLabels[screen] }}</span>
						</label>
						}
					</div>
				</div>

				<div>
					<label class="block text-xs font-medium text-ink mb-1">{{ 'ANALYTICS_FILTER_BAR.FILTERS_BUTTON' | translate }}</label>
					<select [(ngModel)]="createFilterSourceViewId" name="createFilterSourceViewId" class="w-full bg-surface border border-line-strong rounded-xl px-3 py-2 text-xs text-ink focus-visible:outline-2 focus-visible:outline-focus focus-visible:outline-offset-2 font-medium">
						<option value="">{{ 'ANALYTICS_REPORTS.NO_FILTERS_OPTION' | translate }}</option>
						@for (view of savedViews; track view.id) {
						<option [value]="view.id">{{ view.name }}</option>
						}
					</select>
					<p class="text-[11px] text-ink-subtle mt-1">{{ 'ANALYTICS_REPORTS.FILTERS_SOURCE_DESC' | translate }}</p>
				</div>

				<div>
					<label class="block text-xs font-medium text-ink mb-1.5">{{ 'ANALYTICS_REPORTS.FREQUENCY_LABEL' | translate }} <span class="text-negative">*</span></label>
					<div class="flex gap-2">
						<label class="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl border cursor-pointer text-xs font-medium transition-colors"
							[class]="createFrequency === 'WEEKLY' ? 'bg-blue-tint border-blue text-blue' : 'bg-surface border-line text-ink-muted hover:bg-surface-gray'">
							<input type="radio" name="frequency" value="WEEKLY" [(ngModel)]="createFrequency" class="accent-blue" />
							<span>{{ 'ANALYTICS_REPORTS.WEEKLY' | translate }}</span>
						</label>
						<label class="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl border cursor-pointer text-xs font-medium transition-colors"
							[class]="createFrequency === 'MONTHLY' ? 'bg-blue-tint border-blue text-blue' : 'bg-surface border-line text-ink-muted hover:bg-surface-gray'">
							<input type="radio" name="frequency" value="MONTHLY" [(ngModel)]="createFrequency" class="accent-blue" />
							<span>{{ 'ANALYTICS_REPORTS.MONTHLY' | translate }}</span>
						</label>
					</div>
				</div>

				<div>
					<label class="block text-xs font-medium text-ink mb-1">{{ 'ANALYTICS_REPORTS.RECIPIENTS_LABEL' | translate }} <span class="text-negative">*</span></label>
					<textarea
						[(ngModel)]="createRecipientsText"
						name="createRecipientsText"
						rows="2"
						[placeholder]="'ANALYTICS_REPORTS.RECIPIENTS_PLACEHOLDER' | translate"
						class="w-full bg-surface border border-line-strong rounded-xl px-3 py-2 text-xs text-ink focus-visible:outline-2 focus-visible:outline-focus focus-visible:outline-offset-2 font-medium resize-none"></textarea>
					<p class="text-[11px] mt-1" [class]="createRecipients.length > maxRecipients ? 'text-negative' : 'text-ink-subtle'">
						{{ createRecipients.length }}/{{ maxRecipients }} {{ 'ANALYTICS_REPORTS.VALID_EMAILS_DETECTED' | translate }}. {{ 'ANALYTICS_REPORTS.RECIPIENTS_HINT_SUFFIX' | translate }}
					</p>
					@if (createRecipients.length > maxRecipients) {
					<p class="text-[11px] text-negative mt-1">{{ 'ANALYTICS_REPORTS.MAX_RECIPIENTS_EXCEEDED' | translate: { max: maxRecipients } }}</p>
					}
				</div>

				<div class="pt-3 border-t border-line flex items-center justify-end gap-2">
					<button type="button" (click)="closeCreateModal()" class="h-10 px-[18px] border border-line-strong hover:bg-surface-gray text-ink font-medium rounded-full transition-colors cursor-pointer">
						{{ 'BUTTON.CANCEL' | translate }}
					</button>
					<button type="submit" [disabled]="!canSubmitCreate" class="h-10 px-[18px] bg-blue hover:bg-blue-hover text-on-blue font-medium rounded-full transition-colors cursor-pointer disabled:opacity-50">
						{{ (createLoading ? 'ANALYTICS_REPORTS.CREATING' : 'ANALYTICS_REPORTS.CREATE_REPORT_BUTTON') | translate }}
					</button>
				</div>
			</form>
		</div>
	</div>
	}
</div>
